import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { Plus, Edit, Trash2, Search, Fuel } from 'lucide-react'
import toast from 'react-hot-toast'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/Table'
import { Badge } from '../components/ui/Badge'
import { enumsApi } from '../lib/api'
import { formatNumber } from '../lib/utils'
import { useLanguage } from '../contexts/LanguageContext'

const languageLabels = {
  az: 'Azərbaycan',
  ru: 'Русский',
  en: 'English',
  ko: '한국어',
  kk: 'Қазақша',
  ky: 'Кыргызча'
}

export default function FuelTypes() {
  const [filters, setFilters] = useState({
    search: '',
    page: 1,
    limit: 100
  })
  const [deleteId, setDeleteId] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [formData, setFormData] = useState({
    name_az: '',
    name_ru: '',
    name_en: '',
    name_ko: '',
    name_kk: '',
    name_ky: ''
  })
  const { currentLanguage } = useLanguage()
  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery(
    ['fuel-types', filters],
    () => enumsApi.fuelTypes.getAll(filters),
    {
      select: (data) => data?.data?.data,
      keepPreviousData: true,
      retry: 1,
      refetchOnWindowFocus: false
    }
  )

  const deleteMutation = useMutation(
    (id) => enumsApi.fuelTypes.delete(id),
    {
      onSuccess: () => {
        toast.success('Fuel type deleted successfully')
        queryClient.invalidateQueries(['fuel-types'])
        setDeleteId(null)
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to delete fuel type')
      }
    }
  )

  const createMutation = useMutation(
    (data) => enumsApi.fuelTypes.create(data),
    {
      onSuccess: () => {
        toast.success('Fuel type created successfully')
        queryClient.invalidateQueries(['fuel-types'])
        setShowForm(false)
        resetForm()
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create fuel type')
      }
    }
  )

  const updateMutation = useMutation(
    (data) => enumsApi.fuelTypes.update(editingId, data),
    {
      onSuccess: () => {
        toast.success('Fuel type updated successfully')
        queryClient.invalidateQueries(['fuel-types'])
        setShowForm(false)
        setEditingId(null)
        resetForm()
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update fuel type')
      }
    }
  )

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1
    }))
  }

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this fuel type?')) {
      deleteMutation.mutate(id)
    }
  }

  const handleEdit = (fuelType) => {
    window.scrollTo({top: 0, behavior: 'smooth'})
    
    setEditingId(fuelType.id)
    setFormData({
      name_az: fuelType.name_az || fuelType.name,
      name_ru: fuelType.name_ru || fuelType.name,
      name_en: fuelType.name_en || fuelType.name,
      name_ko: fuelType.name_ko || fuelType.name,
      name_kk: fuelType.name_kk || fuelType.name_ru || fuelType.name,
      name_ky: fuelType.name_ky || fuelType.name_ru || fuelType.name
    })
    setShowForm(true)
  }

  const resetForm = () => {
    setFormData({
      name_az: '',
      name_ru: '',
      name_en: '',
      name_ko: '',
      name_kk: '',
      name_ky: ''
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (editingId) {
      updateMutation.mutate(formData)
    } else {
      createMutation.mutate(formData)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Fuel Types</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage fuel type options for vehicles
          </p>
        </div>
        <Button onClick={() => setShowForm(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Fuel Type
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>
              {editingId ? 'Edit Fuel Type' : 'Add New Fuel Type'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Names in Different Languages</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Object.entries(languageLabels).map(([lang, label]) => (
                    <div key={lang}>
                      <label className="label">
                        Name ({label}) *
                        {lang === currentLanguage && (
                          <span className="ml-2 text-xs text-primary-600 font-medium">(Current Language)</span>
                        )}
                      </label>
                      <Input
                        placeholder={`Enter name in ${label}`}
                        value={formData[`name_${lang}`]}
                        onChange={(e) => handleInputChange(`name_${lang}`, e.target.value)}
                        required
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setShowForm(false)
                    setEditingId(null)
                    resetForm()
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  loading={createMutation.isLoading || updateMutation.isLoading}
                >
                  {editingId ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Search className="h-5 w-5 mr-2" />
            Search Fuel Types
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="max-w-md">
            <Input
              placeholder="Search fuel types..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Fuel Types ({data?.count || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="text-center py-12">
              <p className="text-red-600">Error loading fuel types: {error.message}</p>
            </div>
          ) : isLoading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-full mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                </div>
              ))}
            </div>
          ) : data?.results?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.results.map((fuelType) => (
                  <TableRow key={fuelType.id}>
                    <TableCell>
                      <div className="flex items-center">
                        <Fuel className="h-4 w-4 mr-2 text-gray-400" />
                        {fuelType.name}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={fuelType.is_active ? 'success' : 'gray'}>
                        {fuelType.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleEdit(fuelType)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDelete(fuelType.id)}
                          disabled={deleteMutation.isLoading && deleteId === fuelType.id}
                        >
                          {deleteMutation.isLoading && deleteId === fuelType.id ? (
                            <div className="loading-spinner" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <Fuel className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No fuel types</h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by adding your first fuel type.
              </p>
              <div className="mt-6">
                <Button onClick={() => setShowForm(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Fuel Type
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
